import { isDeckShared, type ShareLinkConfig } from '@workspace/seedforge-features/share'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { cn } from '@workspace/ui/lib/utils'
import { usePublicProfilePreviewQuery } from '../../../hooks/api'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'

/** Mirrors the backend's `investorViewNotificationPreferenceSchema` enum. */
export type InvestorViewNotificationPreference = 'INSTANT' | 'DIGEST' | 'OFF'

const NOTIFICATION_OPTIONS: { value: InvestorViewNotificationPreference; label: string }[] = [
  { value: 'INSTANT', label: 'Instant' },
  { value: 'DIGEST', label: 'Daily digest' },
  { value: 'OFF', label: 'Off' },
]

interface ShareLinkHeroProps {
  link: ShareLinkConfig | undefined
  /** The startup's pitch deck, if any — the card shows its first slide only when this link shares it. */
  deckDocumentId: string | undefined
  notificationPreference: InvestorViewNotificationPreference
  onNotificationPreferenceChange: (value: InvestorViewNotificationPreference) => void
}

/** Zone 1 of "Share your Profile": the link hero — URL, copy, OG preview. */
export function ShareLinkHero({
  link,
  deckDocumentId,
  notificationPreference,
  onNotificationPreferenceChange,
}: ShareLinkHeroProps) {
  const { copied, copy } = useCopyToClipboard()
  // Same slide-1 image the /s/ link's og:image uses — the token is the share
  // link's slug, so this reuses the public preview endpoint, no new one needed.
  const preview = usePublicProfilePreviewQuery(link?.slug ?? '', !!link).data?.data

  if (!link) return null

  return (
    <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-border bg-card/30 sm:grid-cols-[1.25fr_0.9fr]">
      <div className="flex flex-col gap-3.5 p-6">
        <h2 className="text-[19px] font-bold text-foreground">Your link</h2>
        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-border bg-card2 px-3.5 py-2.5 text-[14.5px]">
          <span className="text-muted-foreground">
            {link.domain}/<span className="font-semibold text-foreground">{link.slug}</span>
          </span>
          <CtaButton
            size="sm"
            onClick={() => copy(`${link.domain}/${link.slug}`)}
            className="ml-auto shrink-0"
          >
            {copied ? 'Copied' : 'Copy link'}
          </CtaButton>
        </div>
        <p className="text-[13px] text-muted-foreground">
          This link always shows your latest profile.
        </p>
        {/* Pinned to the bottom of the card (which stretches to match the OG
            preview column's height) rather than sitting right under the
            caption — matches the prototype's bottom-anchored placement. */}
        <div className="mt-auto flex flex-wrap items-center gap-2.5 border-t border-border pt-4 text-[13px]">
          <span className="text-muted-foreground">Email me when an investor views:</span>
          <div className="inline-flex overflow-hidden rounded-xl border border-border">
            {NOTIFICATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onNotificationPreferenceChange(opt.value)}
                className={cn(
                  'px-4 py-2.5 text-[13px] font-bold transition-colors',
                  notificationPreference === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card2 text-muted-foreground hover:text-foreground'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-border bg-card2 p-5 sm:border-t-0 sm:border-l">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          What investors see when you paste it
        </p>
        <div className="overflow-hidden rounded-xl border border-border bg-background">
          {/* A link that hides the deck unfurls as text only, so the card
              shows no image area at all rather than a placeholder. */}
          {!isDeckShared(
            link.visibilityConfig,
            deckDocumentId
          ) ? null : preview?.deckPreviewImageUrl ? (
            <img
              src={preview.deckPreviewImageUrl}
              alt="First slide of your pitch deck"
              className="w-full object-contain"
              style={
                preview.deckPreviewImageWidth && preview.deckPreviewImageHeight
                  ? {
                      aspectRatio: `${preview.deckPreviewImageWidth} / ${preview.deckPreviewImageHeight}`,
                    }
                  : undefined
              }
            />
          ) : (
            <div
              className="grid h-[118px] place-items-center"
              style={{ background: 'linear-gradient(135deg, #1E1105, #3A1D07 55%, #F97316 190%)' }}
            >
              <span className="text-xs font-bold tracking-wide text-primary">Deck · page 1</span>
            </div>
          )}
          <div className="border-t border-border p-3 first:border-t-0">
            <p className="text-[13.5px] font-semibold text-foreground">
              {preview?.startupName ?? 'SeedForge'}
            </p>
            {preview?.startupDescription && (
              <p className="mt-0.5 text-xs text-muted-foreground">{preview.startupDescription}</p>
            )}
            <p className="mt-1.5 text-[11px] text-muted-foreground/60">seedforge.com</p>
          </div>
        </div>
      </div>
    </div>
  )
}
